import { motion } from 'framer-motion';
import { Award, Users, Building, Clock } from 'lucide-react';

const stats = [
  { icon: Building, value: '500+', label: 'Projects Completed' },
  { icon: Users, value: '150+', label: 'Team Members' },
  { icon: Award, value: '500+', label: 'Satisfied Customers' },
  { icon: Clock, value: '25+', label: 'Years Experience' },
];

export const AboutSection = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-24 md:py-32 bg-obsidian-paper noise-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gold" />
              <span className="text-gold text-xs uppercase tracking-[0.2em]">About Us</span>
            </div>
            
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              Building <span className="text-primary">Excellence</span> Since 1995
            </h2>
            
            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              AnanthaMeenakshi Group represents the pinnacle of architectural and construction excellence. 
              Our four specialized divisions work in harmony to deliver comprehensive solutions 
              that transform visions into reality.
            </p>
            
            <p className="text-gray-400 leading-relaxed mb-8">
              From groundbreaking designs to premium materials and expert construction, we bring 
              together the finest talent and resources to create spaces that inspire and endure. 
              Our commitment to quality, innovation, and client satisfaction has made us a 
              trusted partner for projects of any scale.
            </p>

            {/* Values */}
            <div className="flex flex-wrap gap-4">
              {['Innovation', 'Quality', 'Integrity', 'Excellence'].map((value) => (
                <span
                  key={value}
                  className="px-4 py-2 border border-gold/30 text-gold text-sm uppercase tracking-wider"
                >
                  {value}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Stats */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-obsidian border border-white/5 p-8 group hover:border-primary/50 transition-colors"
                  data-testid={`stat-card-${index}`}
                >
                  <stat.icon className="w-8 h-8 text-gold mb-4 group-hover:text-primary transition-colors" />
                  <div className="font-heading text-4xl font-bold text-white mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-400 text-sm uppercase tracking-wider">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Image Accent */}
            <div className="mt-6 relative">
              <img
                src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80"
                alt="Architecture"
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 bg-primary/20" />
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gold" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
